// Guard against duplicate script loading
if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.activeElement = null; // Initialize activeElement

      // IE11 fix: Check if event.code exists before using
      this.addEventListener('keyup', (evt) => {
        var keyCode = evt.code || evt.key || evt.keyCode;
        if (keyCode === 'Escape' || keyCode === 27) this.close();
      });
      // Use event delegation for overlay clicks to avoid duplicate listeners after re-render
      this.addEventListener('click', (evt) => {
        if (evt.target && evt.target.id === 'CartDrawer-Overlay') {
          this.close();
        }
      });
      this.setHeaderCartIconAccessibility();
    }

    setHeaderCartIconAccessibility() {
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (!cartLink) return;

      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        // IE11 fix: Check if event.code exists, fallback to keyCode
        var keyCode = event.code || event.key || event.keyCode;
        if (keyCode === 'Space' || keyCode === ' ' || keyCode === 32) {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    }

    open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);
      const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
      if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
      // here the animation doesn't seem to always get triggered. A timeout seem to help
      setTimeout(() => {
        this.classList.add('animate', 'active');
      });

      this.addEventListener(
        'transitionend',
        () => {
          const containerToTrapFocusOn = this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');
          const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
          // Safety check: only trap focus if both elements exist
          if (containerToTrapFocusOn && focusElement) {
            trapFocus(containerToTrapFocusOn, focusElement);
          }
        },
        { once: true }
      );

      document.body.classList.add('overflow-hidden');
    }

    close() {
      this.classList.remove('active');
      removeTrapFocus(this.activeElement);
      document.body.classList.remove('overflow-hidden');
    }

    setSummaryAccessibility(cartDrawerNote) {
      cartDrawerNote.setAttribute('role', 'button');
      cartDrawerNote.setAttribute('aria-expanded', 'false');

      if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
        cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
      }

      cartDrawerNote.addEventListener('click', (event) => {
        event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
      });

      cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
    }

    renderContents(parsedState) {
      if (!parsedState) return;

      // Safari fix: Add null safety
      const drawerInner = this.querySelector('.drawer__inner');
      if (drawerInner && drawerInner.classList.contains('is-empty')) {
        drawerInner.classList.remove('is-empty');
      }
      this.productId = parsedState.id;
      const sectionsToRender = this.getSectionsToRender();

      sectionsToRender.forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement) return;
        const sectionHtml = parsedState.sections ? parsedState.sections[section.id] : null;
        if (!sectionHtml) return;
        const innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
        if (innerHTML !== null) {
          sectionElement.innerHTML = innerHTML;
        }
      });

      // Dispatch cart:updated event so listeners can re-initialize (e.g., appointment badge)
      document.dispatchEvent(new CustomEvent('cart:updated'));

      setTimeout(() => {
        this.open();
      });
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      // Safari fix: Add null safety
      if (!html) return null;
      try {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const element = doc.querySelector(selector);
        return element ? element.innerHTML : null;
      } catch (e) {
        console.warn('Cart drawer: Failed to parse section HTML:', e);
        return null;
      }
    }

    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          selector: '#CartDrawer',
        },
        {
          id: 'cart-icon-bubble',
        },
      ];
    }

    getSectionDOM(html, selector = '.shopify-section') {
      // Safari fix: Add null safety
      if (!html) return null;
      try {
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      } catch (e) {
        console.warn('Cart drawer: Failed to parse section DOM:', e);
        return null;
      }
    }

    setActiveElement(element) {
      this.activeElement = element;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}

// CartDrawerItems extends CartItems from cart.js
// Retry strategy: CartItems must be defined before we can extend it.
// We retry every 50ms for up to 5 seconds to handle async script loading.
// This handles cases where cart.js loads after cart-drawer.js.
if (!customElements.get('cart-drawer-items')) {
  let cartDrawerItemsRetryCount = 0;
  const CART_DRAWER_ITEMS_MAX_RETRIES = 100;

  function defineCartDrawerItems() {
    // Check both global window.CartItems and local CartItems
    const CartItemsClass = window.CartItems || (typeof CartItems !== 'undefined' ? CartItems : undefined);

    if (!CartItemsClass) {
      cartDrawerItemsRetryCount++;
      if (cartDrawerItemsRetryCount >= CART_DRAWER_ITEMS_MAX_RETRIES) {
        console.warn('CartDrawerItems: CartItems not available after max retries');
        return;
      }
      // CartItems not yet loaded, wait and retry
      setTimeout(defineCartDrawerItems, 50);
      return;
    }

    // Only define if not already defined
    if (customElements.get('cart-drawer-items')) return;

    class CartDrawerItems extends CartItemsClass {
      getSectionsToRender() {
        return [
          {
            id: 'CartDrawer',
            section: 'cart-drawer',
            selector: '.drawer__inner',
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
        ];
      }
    }

    customElements.define('cart-drawer-items', CartDrawerItems);
  }

  defineCartDrawerItems();
}
